import { CircleAlert, CircleCheck, Info, TriangleAlert, X, type LucideIcon } from "lucide-react";
import type { ReactElement } from "react";
import { Alert, AlertTitle } from "@/core/components/ui/alert";
import { Button } from "@/core/components/ui/button";
import { useToastStore, type Toast } from "@/core/stores/useToastStore";

const SEVERITY_ICONS: Record<Toast["severity"], LucideIcon> = {
  error: CircleAlert,
  warning: TriangleAlert,
  success: CircleCheck,
  info: Info,
};

// Not portalled: an open Radix modal hides it from assistive tech. No
// pointer-events override, so a click on a toast can't count as outside a dialog.
// Toasts close only with Dismiss or the store's 4 s timer.
export function ToastHost(): ReactElement {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  return (
    <section
      aria-label="Notifications"
      className="fixed inset-x-4 bottom-[max(1rem,env(safe-area-inset-bottom))] z-[1450] flex flex-col-reverse gap-2 sm:left-auto sm:w-96"
    >
      {toasts.map((toast) => {
        const Icon = SEVERITY_ICONS[toast.severity];
        const quiet = toast.severity === "success" || toast.severity === "info";
        return (
          <Alert
            key={toast.id}
            variant={toast.severity === "error" ? "destructive" : "default"}
            className="pr-12"
            {...(quiet ? { role: "status" } : {})}
          >
            <Icon aria-hidden="true" />
            <AlertTitle className="line-clamp-none">{toast.message}</AlertTitle>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Dismiss"
              className="absolute top-1.5 right-1.5"
              onClick={() => {
                dismiss(toast.id);
              }}
            >
              <X aria-hidden="true" />
            </Button>
          </Alert>
        );
      })}
    </section>
  );
}
